import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import type { AppointmentResponse } from '@dataTypes/appointment.type'
import type {
  DashboardPeriod,
  DashboardPeriodParams,
  PeriodBucket,
  PeriodComparisonMetric,
} from './finance.api'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_DASHBOARD')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export type DashboardOverviewPeriodParams = DashboardPeriodParams

export interface ProceduresOverviewSummary {
  totalProceduresCompleted: PeriodComparisonMetric
  averageProductionPerProcedure: PeriodComparisonMetric
  sameDayProcedures: PeriodComparisonMetric
  totalProduction: PeriodComparisonMetric
}

export interface ProceduresByCategoryPeriodSeries {
  category: string
  periodSummary: PeriodBucket[]
}

export interface ProceduresByCategoryTableRow {
  category: string
  procedureCount: number
  production: number
  averageProductionPerProcedure: number
  percentOfTotalProduction: number
  sameDayProductionCount: number
}

export interface ProceduresByProviderTableRow {
  provider: string
  procedureCount: number
  production: number
  averageProductionPerProcedure: number
  sameDayProcedureCount: number
}

export interface ProceduresByTimePeriodTableRow {
  period: string
  procedureCount: number
  production: number
  averageProductionPerProcedure: number
  sameDayProcedureCount: number
}

export interface ProceduresOverviewResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  summary: ProceduresOverviewSummary
  proceduresByCategoryPeriodSummary: ProceduresByCategoryPeriodSeries[]
  byCategory: ProceduresByCategoryTableRow[]
  byProvider: ProceduresByProviderTableRow[]
  byTimePeriod: ProceduresByTimePeriodTableRow[]
}

export interface CollectionsOverviewSummary {
  grossCollection: PeriodComparisonMetric
  netCollection: PeriodComparisonMetric
  insuranceCollection: PeriodComparisonMetric
  patientCollection: PeriodComparisonMetric
}

export interface CollectionsBySourceTableRow {
  source: 'Insurance' | 'Patient'
  collected: number
  adjustment: number
  outstanding: number
  collectionRate: number
}

export interface CollectionsByTimePeriodTableRow {
  period: string
  gross: number
  net: number
  insurance: number
  patient: number
  collectionRate: number
}

export interface CollectionsOverviewResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  summary: CollectionsOverviewSummary
  insuranceCollectionPeriodSummary: PeriodBucket[]
  patientCollectionPeriodSummary: PeriodBucket[]
  bySource: CollectionsBySourceTableRow[]
  byTimePeriod: CollectionsByTimePeriodTableRow[]
}

export const getProceduresOverview = (
  params: DashboardOverviewPeriodParams,
  options?: { signal?: AbortSignal },
): Promise<ProceduresOverviewResult> =>
  axios
    .get<
      ApiResponse<ProceduresOverviewResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/overview/procedures`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getProceduresDetail = (
  params: DashboardOverviewPeriodParams,
  options?: { signal?: AbortSignal },
): Promise<ProceduresOverviewResult> =>
  axios
    .get<
      ApiResponse<ProceduresOverviewResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/overview/procedures/detail`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getCollectionsOverview = (
  params: DashboardOverviewPeriodParams,
  options?: { signal?: AbortSignal },
): Promise<CollectionsOverviewResult> =>
  axios
    .get<
      ApiResponse<CollectionsOverviewResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/overview/collections`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getCollectionsDetail = (
  params: DashboardOverviewPeriodParams,
  options?: { signal?: AbortSignal },
): Promise<CollectionsOverviewResult> =>
  axios
    .get<
      ApiResponse<CollectionsOverviewResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/overview/collections/detail`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export interface TodayScheduleAppointmentsResult {
  appointments: AppointmentResponse[]
}

/** Day calendar list. Extra period params from the dashboard cache are not sent. */
export const getTodayScheduleAppointments = (
  params: DashboardPeriodParams,
  options?: { signal?: AbortSignal },
): Promise<AppointmentResponse[]> =>
  axios
    .get<ApiResponse<TodayScheduleAppointmentsResult>>(
      `${BUSINESS_LAYER_API}/api/v1/dashboard/overview/today-schedule`,
      {
        params: { clinicId: params.clinicId, date: params.date },
        signal: options?.signal,
      },
    )
    .then((response) => response.data.data.appointments ?? [])
